import { useEffect } from "react";

import { allMainColors } from "@foodpilot/foods";

import { ScoreService } from "src/context/EntityBasedScore/ScoreService";
import { URLHandler } from "src/services/URLHandler";
import { useApplicationParameters } from "src/api/client-api/appParams";

import { usePuckActionPlan } from "../../../contexts/PuckActionPlanContext";
import { usePuckCompanyScore } from "../../../contexts/PuckCompanyScoreContext";
import { formatChartData, extractYearFromLabel, calculatePercentageDifference } from "../utils/formatters";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

export interface ActionPlanDataProps {
    actionPlanId?: string;
    scoreId?: number | null;
    variationId?: number | null;
    customGoalValue?: number;
    customGoalYear?: number;
    customGoalUnit?: string;
}

export const useActionPlanData = (options: ActionPlanDataProps) => {
    const { actionPlanId, scoreId, variationId, customGoalValue, customGoalYear, customGoalUnit } = options;
    const { isFeatureActivated } = useApplicationParameters();
    const { selectedCompany } = useFoodpilotContext();
    const {
        actionPlanData,
        isLoading: isActionPlanLoading,
        precision,
        setVariation: setActionPlanVariation,
    } = usePuckActionPlan();
    const { companyPost, isLoading: isCompanyLoading, setVariation, scoreTitle } = usePuckCompanyScore();

    useEffect(() => {
        if (scoreId === undefined || variationId === undefined) {
            return;
        }

        if (scoreId) {
            setVariation(scoreId, variationId);
            setActionPlanVariation(scoreId, variationId);
        }
    }, [variationId, scoreId]);

    const isLoading = isActionPlanLoading || isCompanyLoading;
    const hasError = !isLoading && (!companyPost || (!actionPlanData.length && actionPlanId));

    const activePlan = actionPlanData.find((plan) => {
        if (actionPlanId && plan.metadata?.actionPlan?.id === actionPlanId) {
            return true;
        }

        const scoreMatches = scoreId && plan.scoreId === scoreId;
        if (scoreMatches) {
            return true;
        }

        return false;
    });

    const diagnosticScoreFromCompany = companyPost?.scores && scoreId ? companyPost.scores[scoreId] : undefined;
    const diagnosticScore = (diagnosticScoreFromCompany?.score as number) || 0;
    const diagnosticYearValue = extractYearFromLabel(companyPost?.campaign?.label);
    const diagnosticSSQCompletions = companyPost?.completions?.ssq.result || 0;

    const filteredScoreItems =
        scoreId ?
            activePlan?.metadata?.scoresWithActionPost?.details.filter((item) => {
                return item.score2 && "scoreId" in item.score2 && item.score2.scoreId === scoreId;
            })
        :   [];

    const projectedScoreItem =
        filteredScoreItems && filteredScoreItems.length > 0 ?
            filteredScoreItems[filteredScoreItems.length - 1]
        :   activePlan?.metadata?.scoresWithActionPost?.lastItem;

    const matchingScoreDetail = activePlan?.metadata?.scoresWithActionPost?.details?.find(
        (detail) => scoreId && detail.score2 && "id" in detail.score2 && detail.score2.id === scoreId,
    );

    const projectedRawScore =
        matchingScoreDetail?.score ||
        activePlan?.metadata?.scoresWithActionPost?.lastItem?.score ||
        (scoreId ? diagnosticScoreFromCompany?.score : null) ||
        0;

    const projectedScore = ScoreService.normalizeScore(projectedRawScore, precision).value ?? 0;
    const projectedYearLabel = projectedScoreItem?.campaign?.label || "";
    const projectedYearValue = extractYearFromLabel(projectedYearLabel, diagnosticYearValue);

    const goalValue = customGoalValue !== undefined ? customGoalValue : projectedScore;
    const goalYear = customGoalYear !== undefined ? customGoalYear : projectedYearValue;
    const goalUnit = customGoalUnit || diagnosticScoreFromCompany?.unit || "";

    const actionCount = activePlan?.metadata?.actionPlan?.actionPlanActions?.length || 0;
    const unit = diagnosticScoreFromCompany?.unit || "";

    const diagnosticToProjectedDifference = calculatePercentageDifference(projectedScore, diagnosticScore);
    const projectedToGoalDifference = calculatePercentageDifference(goalValue, projectedScore);
    const diagnosticToGoalDifference = calculatePercentageDifference(goalValue, diagnosticScore);

    const diagnosticScoreHeadings = diagnosticScoreFromCompany?.dimensions?.[0]?.values;
    const projectedScoreHeadings = projectedScoreItem?.score2?.dimensions?.[0]?.values;

    const diagnosticScoreChart = formatChartData(diagnosticScoreHeadings);
    const projectedScoreChart = formatChartData(projectedScoreHeadings);

    const datavizColors = Object.values(allMainColors).slice(0, diagnosticScoreChart.values.length);
    const legendItems = diagnosticScoreChart.labels.map((label, index) => ({
        color: datavizColors[index],
        label,
    }));

    const diagnosticCTAURL = selectedCompany?.id ? URLHandler.company.ssq(selectedCompany.id) : "";
    const projectedCTAURL =
        isFeatureActivated("FS-actionPlan") && selectedCompany?.id ?
            URLHandler.company.actions.list(selectedCompany.id)
        :   "";

    return {
        isLoading,
        hasError,
        precision,

        scoreTitle,

        diagnosticScore,
        diagnosticScoreChart,
        diagnosticYearValue,
        diagnosticSSQCompletions,
        diagnosticCTAURL,

        projectedScore,
        projectedScoreChart: {
            values: projectedScoreChart.values,
            labels: diagnosticScoreChart.labels,
        },
        projectedYearValue,
        actionCount,
        projectedCTAURL,

        goalValue,
        goalYear,
        goalUnit,

        diagnosticToProjectedDifference,
        projectedToGoalDifference,
        diagnosticToGoalDifference,

        unit,
        legendItems,
    };
};
